/**
 * Lazy-route chunk prefetcher.
 *
 * Route components are split via `React.lazy(() => import(...))` so the
 * initial bundle stays small. The flip side is that the *first* navigation
 * to a new route still pays a network round-trip to fetch its JS chunk —
 * typically 200-700 ms on a 4G connection, longer on cold edges.
 *
 * This module centralises the import functions so we can warm those chunks
 * ahead of time: on idle from the landing page, and on pointer/focus over
 * the nav links that lead to them. Vite preserves the resulting chunk
 * cache, so when the user actually clicks, the component is already
 * resolved and `Suspense` doesn't even flash.
 *
 * Each prefetcher is safe to call repeatedly (the dynamic import is
 * memoised by the bundler) and is a no-op if the chunk fails — we don't
 * want a transient network blip to surface as an error toast.
 */

type Prefetcher = () => Promise<unknown>;

// Keep these in sync with the lazy() calls in src/App.tsx. Using the same
// import paths is what lets Vite/Rollup dedupe to a single chunk.
const importers = {
  account: () => import("@/pages/Account.tsx"),
  recipe: () => import("@/pages/Recipe.tsx"),
  sharedRecipe: () => import("@/pages/SharedRecipe.tsx"),
  myRecipes: () => import("@/pages/MyRecipes.tsx"),
  menus: () => import("@/pages/Menus.tsx"),
  menuDetail: () => import("@/pages/MenuDetail.tsx"),
  menuPrint: () => import("@/pages/MenuPrint.tsx"),
  upgrade: () => import("@/pages/Upgrade.tsx"),
  bulk: () => import("@/pages/Bulk.tsx"),
} satisfies Record<string, Prefetcher>;

export type RouteKey = keyof typeof importers;

const inflight = new Set<RouteKey>();

const safePrefetch = (key: RouteKey) => {
  if (inflight.has(key)) return;
  inflight.add(key);
  importers[key]().catch(() => {
    // Allow a future explicit navigation to retry — clear the marker.
    inflight.delete(key);
  });
};

/** Prefetch a single route chunk. Safe to call from event handlers. */
export const prefetchRoute = (key: RouteKey) => safePrefetch(key);

/**
 * Schedule a low-priority prefetch of the routes a logged-in user most
 * often reaches from the landing page. Uses `requestIdleCallback` when
 * available so we never compete with first-paint work.
 */
export const schedulePostLoadPrefetch = (keys: RouteKey[]) => {
  if (typeof window === "undefined") return;
  const ric =
    (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
      .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
  ric(
    () => {
      // Avoid prefetching on Save-Data / 2G connections where every byte
      // counts more than perceived nav latency.
      const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
      if (conn?.saveData) return;
      if (conn?.effectiveType && /^(slow-2g|2g)$/.test(conn.effectiveType)) return;
      for (const key of keys) safePrefetch(key);
    },
    { timeout: 4000 },
  );
};
